import useLockIterationDetails from 'queries/mutate/lockIterationDetails';
import useFetchSpecificCampaign from 'queries/fetch/fetchSpecificCampaign.ts';
import { ITERATION_STATUS } from 'schemas/CampaignApiDetails.schema';
import useCampaign from './useCampaign';
import useCampaignNavigate from './useCampaignNavigate';
import useIterationNavigate from './useIterationNavigate';
import useAuth from './useAuth';

interface UseIterationLockParams {
  teamId: string;
  campaignId: string;
  iterationId: string;
}

const useIterationLock = ({ teamId, campaignId, iterationId }: UseIterationLockParams) => {
  const { user } = useAuth();
  const { getDynamoIterationDetails, campaign } = useCampaign();
  const { navigateToCampaign } = useCampaignNavigate();
  const { navigateToIteration } = useIterationNavigate();
  const fetchSpecificCampaign = useFetchSpecificCampaign({
    teamId,
    campaignId,
  });
  const lockIterationMutation = useLockIterationDetails();

  const dynamoIterationDetails = getDynamoIterationDetails(iterationId as string);

  const lockIteration = async () => {
    await lockIterationMutation.mutateAsync({
      teamId,
      campaignId,
      iterationId,
      newState: 'locked',
    });

    await fetchSpecificCampaign.refetch();
  };

  const unlockIteration = async (nextPage?: string) => {
    await lockIterationMutation.mutateAsync({
      teamId,
      campaignId,
      iterationId,
      newState: 'unlocked',
    });

    await fetchSpecificCampaign.refetch();

    if (nextPage) {
      navigateToCampaign(nextPage);
    }
  };

  const lockIterationIfRequired = async () => {
    const lockedById = dynamoIterationDetails?.locked_by?.id;

    if (!iterationId) return;

    if (campaign.CampaignConfig.Name === '') {
      navigateToCampaign('/summary');
      return;
    }

    if (lockedById) {
      if (lockedById !== user.userId) {
        navigateToIteration('/summary');
      }
      return;
    }

    if (dynamoIterationDetails?.status === ITERATION_STATUS.DRAFT) {
      await lockIteration();
    }
  };

  return {
    lockIteration,
    lockIterationIfRequired,
    unlockIteration,
    error: lockIterationMutation.error,
    isPending: lockIterationMutation.isPending,
  };
};

export default useIterationLock;
